import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, CalendarDays, Zap, Leaf, Mountain, Plane, Dumbbell } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { useToast } from "@/components/system";
import { formatError } from "@/lib/format-error";
import { PrimaryButton, SecondaryButton } from "@/components/forms";

type ProgramTemplate = {
  id: string;
  slug: string;
  name: string;
  duration_days: number;
  description: string | null;
  diet_pattern_compatibility: string[];
  training_phase: string;
  kcal_modifier_pct: number;
  protein_target_g_per_kg: number | null;
  carb_share_pct: number | null;
};

type Activation = {
  id: string;
  program_template_id: string;
  start_date: string;
  end_date: string;
  state: string;
};

const PHASE_LABELS: Record<string, string> = {
  base_aerobic: "Base aerobic",
  build: "Build",
  peak: "Peak",
  taper: "Taper",
  recovery: "Recovery",
  off_season: "Off-season",
  strength_block: "Strength block",
  travel: "Travel",
};

function phaseIcon(phase: string) {
  if (phase === "recovery") return Zap;
  if (phase === "taper") return Mountain;
  if (phase === "travel") return Plane;
  if (phase === "strength_block") return Dumbbell;
  return Leaf;
}

export function HealthPrograms() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const toast = useToast();
  const qc = useQueryClient();
  const [activating, setActivating] = useState<string | null>(null);

  const templates = useQuery({
    queryKey: ["program_templates"],
    queryFn: async (): Promise<ProgramTemplate[]> => {
      const { data, error } = await supabase
        .from("program_template" as never)
        .select("id, slug, name, duration_days, description, diet_pattern_compatibility, training_phase, kcal_modifier_pct, protein_target_g_per_kg, carb_share_pct")
        .order("training_phase");
      if (error) throw error;
      return (data ?? []) as unknown as ProgramTemplate[];
    },
  });

  const activations = useQuery({
    queryKey: ["program_activations", userId],
    enabled: Boolean(userId),
    queryFn: async (): Promise<Activation[]> => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from("user_program_activation" as never)
        .select("id, program_template_id, start_date, end_date, state")
        .eq("user_id", userId)
        .eq("state", "active")
        .order("start_date", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as Activation[];
    },
  });

  const activate = useMutation({
    mutationFn: async (templateId: string) => {
      if (!userId) throw new Error("Not signed in");
      const tmpl = templates.data?.find((t) => t.id === templateId);
      if (!tmpl) throw new Error("Program not found");
      const startDate = new Date().toISOString().slice(0, 10);
      const endDate = new Date(Date.now() + (tmpl.duration_days - 1) * 86400000)
        .toISOString()
        .slice(0, 10);
      const { error } = await supabase
        .from("user_program_activation" as never)
        .insert({
          user_id: userId,
          program_template_id: templateId,
          start_date: startDate,
          end_date: endDate,
          state: "active",
        } as never);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["program_activations", userId] });
      toast.show("Program started.");
      setActivating(null);
    },
    onError: (e) => {
      toast.show(formatError(e));
      setActivating(null);
    },
  });

  const deactivate = useMutation({
    mutationFn: async (activationId: string) => {
      const { error } = await supabase
        .from("user_program_activation" as never)
        .update({ state: "cancelled" } as never)
        .eq("id", activationId);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["program_activations", userId] });
      toast.show("Program cancelled.");
    },
    onError: (e) => toast.show(formatError(e)),
  });

  const activeByTemplate = new Map(
    (activations.data ?? []).map((a) => [a.program_template_id, a]),
  );

  return (
    <article>
      <Link
        to="/health"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          color: "var(--fg-3)",
          fontSize: "var(--type-body-s)",
          textDecoration: "none",
          marginBottom: "var(--space-3)",
        }}
      >
        <ChevronLeft size={14} strokeWidth={1.5} /> Health
      </Link>

      <header style={{ marginBottom: "var(--space-6)" }}>
        <p
          style={{
            fontSize: "var(--type-micro)",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "var(--fg-3)",
            margin: 0,
          }}
        >
          Health
        </p>
        <h1
          style={{
            fontFamily: "var(--font-serif)",
            fontStyle: "italic",
            fontWeight: 500,
            fontSize: "var(--type-display-m)",
            margin: "var(--space-2) 0 var(--space-2)",
          }}
        >
          Programs
        </h1>
        <p style={{ margin: 0, color: "var(--fg-2)", fontSize: "var(--type-body-s)", lineHeight: 1.55 }}>
          Training-block-aligned meal sequences. Each program is a 7-day prescription
          shaped by your current training phase, not a generic diet.
        </p>
      </header>

      {templates.isLoading ? (
        <p style={{ color: "var(--fg-3)" }}>Loading…</p>
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: "var(--space-3)" }}>
          {(templates.data ?? []).map((t) => {
            const active = activeByTemplate.get(t.id);
            const Icon = phaseIcon(t.training_phase);
            return (
              <li key={t.id}>
                <ProgramCard
                  template={t}
                  activation={active}
                  Icon={Icon}
                  onActivate={() => {
                    setActivating(t.id);
                    activate.mutate(t.id);
                  }}
                  onDeactivate={() => {
                    if (active && confirm("Cancel this program?")) {
                      deactivate.mutate(active.id);
                    }
                  }}
                  activating={activating === t.id && activate.isPending}
                />
              </li>
            );
          })}
        </ul>
      )}
    </article>
  );
}

function ProgramCard({
  template,
  activation,
  Icon,
  onActivate,
  onDeactivate,
  activating,
}: {
  template: ProgramTemplate;
  activation: Activation | undefined;
  Icon: typeof Leaf;
  onActivate: () => void;
  onDeactivate: () => void;
  activating: boolean;
}) {
  const isActive = Boolean(activation);
  return (
    <article
      style={{
        padding: "var(--space-4)",
        background: "var(--bg-2)",
        border: `1px solid ${isActive ? "var(--accent-dim)" : "var(--hair)"}`,
        borderRadius: "var(--radius-card)",
        display: "grid",
        gap: "var(--space-3)",
      }}
    >
      <header style={{ display: "flex", alignItems: "flex-start", gap: "var(--space-3)" }}>
        <div
          style={{
            width: 40,
            height: 40,
            background: isActive ? "var(--accent-wash)" : "var(--bg-3)",
            color: isActive ? "var(--accent)" : "var(--fg-3)",
            borderRadius: "var(--radius-card)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon size={20} strokeWidth={1.5} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", flexWrap: "wrap" }}>
            <span style={{ fontWeight: 500, fontSize: "var(--type-body)", color: "var(--fg)" }}>
              {template.name}
            </span>
            {isActive && (
              <span
                style={{
                  fontSize: "var(--type-micro)",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "var(--accent)",
                  background: "var(--accent-wash)",
                  padding: "1px 8px",
                  borderRadius: "var(--radius-chip)",
                }}
              >
                Active
              </span>
            )}
          </div>
          <div
            style={{
              display: "flex",
              gap: "var(--space-3)",
              marginTop: 4,
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontSize: "var(--type-meta)", color: "var(--fg-3)" }}>
              {PHASE_LABELS[template.training_phase] ?? template.training_phase}
            </span>
            <span style={{ fontSize: "var(--type-meta)", color: "var(--fg-3)", display: "inline-flex", alignItems: "center", gap: 3 }}>
              <CalendarDays size={11} strokeWidth={1.5} />
              {template.duration_days}d
            </span>
            {template.kcal_modifier_pct !== 0 && (
              <span style={{ fontSize: "var(--type-meta)", color: "var(--fg-3)" }}>
                {template.kcal_modifier_pct > 0 ? "+" : ""}{template.kcal_modifier_pct}% kcal
              </span>
            )}
            {template.protein_target_g_per_kg && (
              <span style={{ fontSize: "var(--type-meta)", color: "var(--fg-3)" }}>
                {template.protein_target_g_per_kg}g/kg protein
              </span>
            )}
            {template.carb_share_pct && (
              <span style={{ fontSize: "var(--type-meta)", color: "var(--fg-3)" }}>
                {template.carb_share_pct}% carbs
              </span>
            )}
          </div>
        </div>
      </header>

      {template.description && (
        <p style={{ margin: 0, fontSize: "var(--type-body-s)", color: "var(--fg-2)", lineHeight: 1.55 }}>
          {template.description}
        </p>
      )}

      <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
        {template.diet_pattern_compatibility.map((tag) => (
          <span
            key={tag}
            style={{
              fontSize: "var(--type-micro)",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: "var(--fg-3)",
              background: "var(--bg-3)",
              border: "1px solid var(--hair)",
              borderRadius: "var(--radius-chip)",
              padding: "2px 8px",
            }}
          >
            {tag.replace(/_/g, " ")}
          </span>
        ))}
      </div>

      {isActive && activation && (
        <div
          style={{
            padding: "var(--space-3)",
            background: "var(--accent-wash)",
            borderRadius: "var(--radius-chip)",
            fontSize: "var(--type-body-s)",
            color: "var(--fg-2)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "var(--space-3)",
          }}
        >
          <span>
            {activation.start_date} to {activation.end_date}
          </span>
          <SecondaryButton onClick={onDeactivate}>Cancel</SecondaryButton>
        </div>
      )}

      {!isActive && (
        <div>
          <PrimaryButton onClick={onActivate} disabled={activating}>
            {activating ? "Starting…" : `Start ${template.duration_days}-day program`}
          </PrimaryButton>
        </div>
      )}
    </article>
  );
}
